import React, { useMemo } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import { formatExecutionTimestampWithZone } from "../../Utils/timezone";
import { useDisplayTimeZone } from "../../Utils/displayTimeZone";

type TradeHeaderProps = {
  currencyPair: string;
  tradeType: "BUY" | "SELL";
  executionTimestampUtc: string;
  executionSpotRate?: number | null;
  executionForwardPoints?: number | null;
  expiryDateUtc?: string;
};

function fmtNumber(x: number, dp = 5) {
  if (!Number.isFinite(x)) return "—";
  return x.toFixed(dp);
}

const useHeaderStyles = makeStyles((theme: any) => ({
  header: {
    position: "sticky",
    top: 0,
    zIndex: 0,
    backgroundColor: theme.globallink.colors.DarkGrey[500],
    padding: "10px 10px 8px 10px",
  },
  row: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.09)",
    color: "#E0E0E0",
    fontSize: 13,
    lineHeight: "16px",
    whiteSpace: "nowrap",
  },
  chipBuy: {
    background: "rgba(76, 175, 80, 0.15)",
    border: "1px solid rgba(76, 175, 80, 0.3)",
  },
  chipSell: {
    background: "rgba(244, 67, 54, 0.15)",
    border: "1px solid rgba(244, 67, 54, 0.3)",
  },
  key: { opacity: 0.7 },
  value: { fontWeight: 700 },
  right: {
    marginLeft: "auto",
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
}));

export function TradeAnalysisTradeHeader({
  currencyPair,
  tradeType,
  executionTimestampUtc,
  executionSpotRate,
  expiryDateUtc,
  executionForwardPoints,
}: TradeHeaderProps) {
  const theme = useTheme();
  const classes: any = useHeaderStyles({ theme });
  const { timeZone } = useDisplayTimeZone();
  const executionForwardRate = useMemo(() => {
    if (executionSpotRate == null || executionForwardPoints == null) return null;
    if (!Number.isFinite(executionSpotRate) || !Number.isFinite(executionForwardPoints)) {
      return null;
    }
    return executionSpotRate + executionForwardPoints;
  }, [executionSpotRate, executionForwardPoints]);

  return (
    <div className={classes.header}>
      <div className={classes.row}>
        <span className={classes.chip}>
          <span className={classes.key}>Pair</span>
          <span className={classes.value}>{currencyPair}</span>
        </span>

        <span
          className={`${classes.chip} ${
            tradeType === "BUY" ? classes.chipBuy : classes.chipSell
          }`}
        >
          <span className={classes.key}>Type</span>
          <span className={classes.value}>{tradeType}</span>
        </span>

        <span className={classes.chip}>
          <span className={classes.key}>Exec Timestamp</span>
          <span className={classes.value}>
            {formatExecutionTimestampWithZone(executionTimestampUtc, timeZone)}
          </span>
        </span>

        <span className={classes.chip}>
          <span className={classes.key}>Exec Spot Rate</span>
          <span className={classes.value}>
            {executionSpotRate == null
              ? "—"
              : fmtNumber(
                  executionSpotRate,
                  currencyPair.includes("JPY") ? 3 : 5
                )}
          </span>
        </span>

        <span className={classes.chip}>
          <span className={classes.key}>Exec Forward Point</span>
          <span className={classes.value}>
            {executionForwardPoints == null
              ? "—"
              : fmtNumber(
                  executionForwardPoints,
                  currencyPair.includes("JPY") ? 3 : 5
                )}
          </span>
        </span>
        <span className={classes.chip}>
          <span className={classes.key}>Exec Forward Rate</span>
          <span className={classes.value}>
            {executionForwardRate == null
              ? "—"
              : fmtNumber(
                  executionForwardRate,
                  currencyPair.includes("JPY") ? 3 : 5
                )}
          </span>
        </span>

        <span className={classes.chip}>
          <span className={classes.key}>Expiry</span>
          <span className={classes.value}>{expiryDateUtc || "—"}</span>
        </span>
      </div>
    </div>
  );
}
